/* eslint-disable @typescript-eslint/ban-types */
import { FC, useEffect, useRef, useState, useCallback } from 'react';
import { Alert, Tooltip, notification, Tag } from 'antd';

import { Modal, Search, Button, DataGrid } from '@hydrodigital/design-system';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';

import useDebounce from '@/hooks/useDebounce';

import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import './index.scss';
import InfrastructureService from '../../domain/Infrastructure/InfraestructureService';
import GisService from '../../domain/Gis/GisService';
import dayjs from 'dayjs';
import { Types } from '@/domain/Infrastructure/Types';

const infrastructureService = new InfrastructureService();
const gisService = new GisService();

interface DataType {
	key: string;
	uploadId: string;
	uploadName: number;
	gisFileUploadedDTOS: {
		locationName: string;
		infraTypeName: string;
		createdBy: string;
	}[];
	uploadDate: string;
	uploadStatusMessage: string;
	uploadStatus: string;
}

const GisTable: FC<{
	handleUpdateGisFiles: any;
	files: Array<any>;
	onChangePagination: any;
	pagination: any;
	setInputSearch: Function;
	setTypeGis: Function;
}> = ({
	handleUpdateGisFiles,
	files,
	onChangePagination,
	pagination,
	setInputSearch,
	setTypeGis,
}) => {
	const navigate = useNavigate();
	const [types, setTypes] = useState<Types[]>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const debouncedSearch = useDebounce(searchValue, 500);
	const isInternalChange = useRef(false);
	const [paginationState, setPaginationState] = useState({
		pageIndex: pagination.page - 1,
		pageSize: pagination.pageSize || 10,
	});

	
	useEffect(() => {
		if (!isInternalChange.current) {
			setPaginationState({
				pageIndex: pagination.page - 1,
				pageSize: pagination.pageSize || 10,
			});
		}
		isInternalChange.current = false;
	}, [pagination.page, pagination.pageSize]);

	
	const handlePaginationChange = (updater: any) => {
		const newState = typeof updater === 'function' ? updater(paginationState) : updater;
		isInternalChange.current = true;
		setPaginationState(newState);
		onChangePagination(newState.pageIndex + 1, newState.pageSize);
	};

	useEffect(() => {
		setInputSearch(debouncedSearch || null);
	}, [debouncedSearch]);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [gisSelect, setGisSelect] = useState<any | null>(null);

	const columns: ColumnsType<DataType> = [
		{
			title: 'File Name',
			dataIndex: 'uploadName',
			key: 'uploadName',
			render: (_, { uploadName }) => (
				<>
					<span className="table-data-gis">{uploadName}</span>
				</>
			),
		},
		{
			title: 'Location',
			dataIndex: 'gisFileUploadedDTOS',
			key: 'gisFileUploadedDTOS',
			render: (_, { gisFileUploadedDTOS }) => (
				<>
					{gisFileUploadedDTOS.map((gis, index) => {
						return (
							<span className="table-data-gis" key={index}>
								{gis.locationName}
							</span>
						);
					})}
				</>
			),
		},
		{
			title: 'Type',
			dataIndex: 'gisFileUploadedDTOS',
			key: 'gisFileUploadedDTOS',
			render: (_, { gisFileUploadedDTOS }) => (
				<>
					{gisFileUploadedDTOS.map((gis, index) => {
						return (
							<span className="table-data-gis" key={index}>
								{gis.infraTypeName.charAt(0).toUpperCase() +
									gis.infraTypeName.slice(1).toLowerCase()}
							</span>
						);
					})}
				</>
			),
		},
		{
			title: 'Uploaded by',
			dataIndex: ['uploadDate', 'gisFileUploadedDTOS'],
			key: 'gisFileUploadedDTOS',
			render: (_, { uploadDate, gisFileUploadedDTOS }) => (
				<>
					{gisFileUploadedDTOS.map((gis, index) => {
						return (
							<div key={index}>
								<span className="table-data-gis">{gis.createdBy}</span>
								<br></br>
								<span className="date-gis">
									{dayjs(uploadDate).format('YYYY/MM/DD [at] hh:mm a')}
								</span>
							</div>
						);
					})}
				</>
			),
		},
		{
			title: 'Upload Status',
			dataIndex: 'uploadStatus',
			key: 'uploadStatus',
			render: (_, { uploadStatus }) => {
				const fontColor = '#232B36';
				let color = '#FFEECB';
				if (uploadStatus === 'COMPLETED') {
					color = '#C0EFDE';
				}
				if (uploadStatus === 'IN PROGRESS') {
					color = '#FFEECB';
				}
				if (uploadStatus === 'FAILED') {
					color = '#FFD3D3';
				}
				return (
					uploadStatus && (
						<Tag color={color} style={{ color: fontColor }}>
							{uploadStatus}
						</Tag>
					)
				);
			},
		},
		{
			title: 'Upload Status Message',
			dataIndex: 'uploadStatusMessage',
			key: 'uploadStatusMessage',
			render: (_, { uploadStatusMessage }) => (
				<span className="table-data-gis">{uploadStatusMessage}</span>
			),
		},
		{
			title: 'Actions',
			dataIndex: 'uploadId',
			key: 'uploadId',
			render: (_, { uploadName, uploadId }) => (
				<>
					<Tooltip title="Delete">
						<DeleteOutlined
							className="mr-1"
							style={{ fontSize: '1.5rem' }}
							onClick={() => {
								setGisSelect({
									label: uploadName,
									id: uploadId,
								});
								setShowDeleteModal(true);
							}}
							rev={undefined}
						/>
					</Tooltip>
				</>
			),
		},
	];

	useEffect(() => {
		const res = infrastructureService.getInfrastructureTypes();
		res.then((value) => {
			setTypes(value);
		});
	}, []);

	
	const fetchInterval = useRef<ReturnType<typeof setInterval> | null>(null);

	const hasInProgressFiles = useCallback(() => {
		return files.some((file: any) => file.uploadStatus === 'IN PROGRESS');
	}, [files]);

	useEffect(() => {
		
		if (fetchInterval.current) {
			clearInterval(fetchInterval.current);
			fetchInterval.current = null;
		}

		
		if (hasInProgressFiles()) {
			fetchInterval.current = setInterval(() => {
				handleUpdateGisFiles(false);
			}, 10000);
		}

		return () => {
			if (fetchInterval.current) {
				clearInterval(fetchInterval.current);
				fetchInterval.current = null;
			}
		};
	}, [hasInProgressFiles, handleUpdateGisFiles]);

	
	const gridColumns = [
		{
			header: 'File Name',
			accessorKey: 'uploadName',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Location',
			accessorKey: 'gisFileUploadedDTOS',
			cell: ({ row }: any) => {
				const items = row.original?.gisFileUploadedDTOS ?? [];
				return (
					<>
						{items.map((gis: any, index: number) => (
							<span className="table-data-gis" key={index}>
								{gis.locationName}
							</span>
						))}
					</>
				);
			},
		},
		{
			header: 'Type',
			accessorKey: 'gisFileUploadedDTOS',
			cell: ({ row }: any) => {
				const items = row.original?.gisFileUploadedDTOS ?? [];
				return (
					<>
						{items.map((gis: any, index: number) => (
							<span className="table-data-gis" key={index}>
								{gis.infraTypeName.charAt(0).toUpperCase() +
									gis.infraTypeName.slice(1).toLowerCase()}
							</span>
						))}
					</>
				);
			},
		},
		{
			header: 'Uploaded by',
			accessorKey: 'uploadDate',
			cell: ({ row }: any) => {
				const uploadDate = row.original?.uploadDate;
				const items = row.original?.gisFileUploadedDTOS ?? [];
				return (
					<>
						{items.map((gis: any, index: number) => (
							<div key={index}>
								<span className="table-data-gis">{gis.createdBy}</span>
								<br />
								<span className="date-gis">
									{dayjs(uploadDate).format('YYYY/MM/DD [at] hh:mm a')}
								</span>
							</div>
						))}
					</>
				);
			},
		},
		{
			header: 'Upload Status',
			accessorKey: 'uploadStatus',
			cell: ({ cell }: any) => {
				const uploadStatus = cell.getValue();
				const fontColor = '#232B36';
				let color = '#FFEECB';
				if (uploadStatus === 'COMPLETED') color = '#C0EFDE';
				if (uploadStatus === 'IN PROGRESS') color = '#FFEECB';
				if (uploadStatus === 'FAILED') color = '#FFD3D3';
				return (
					uploadStatus && (
						<Tag color={color} style={{ color: fontColor }}>
							{uploadStatus}
						</Tag>
					)
				);
			},
		},
		{
			header: 'Upload Status Message',
			accessorKey: 'uploadStatusMessage',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Actions',
			accessorKey: 'uploadId',
			cell: ({ row }: any) => {
				const { uploadName, uploadId } = row.original || {};
				return (
					<Tooltip title="Delete">
						<DeleteOutlined
							className="mr-1"
							style={{ fontSize: '1.5rem' }}
							onClick={() => {
								setGisSelect({ label: uploadName, id: uploadId });
								setShowDeleteModal(true);
							}}
							rev={undefined}
						/>
					</Tooltip>
				);
			},
		},
	];

	return (
		<div>
			<div className="flex justify-between">
				<div style={{ flexDirection: 'row', display: 'flex' }}>
					<Search
						placeholder="Search"
						size="small"
						value={searchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						onClear={() => {
							setSearchValue('');
						}}
						showClearButton
					/>
				<div style={{ marginLeft: 30, width: 160 }}>
						<Combobox
							items={types.map((t: any) => ({
								value: String(t.value),
								label: t.label,
							}))}
							onValueChange={(val) => setTypeGis(Number(val))}
							onClear={() => setTypeGis(undefined)}
							size="small"
						>
							<ComboboxInput placeholder="Type" />
							<ComboboxContent>
								<ComboboxEmpty>No types found.</ComboboxEmpty>
								<ComboboxList>
									{(item) => (
										<ComboboxItem key={item.value} value={item}>
											{item.label}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
				</div>

				<div>
					<Button
						size="small"
						style={{
							fontWeight: 700,
							fontSize: 14,
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						label="Upload GIS Files"
						className="mr-5 boton-inicio flex items-center"
						onClick={() => {
							navigate(`/upload/gisfiles`);
						}}
					/>
				</div>
			</div>
			<DataGrid
				className="mt-3"
				data={files}
				columns={gridColumns}
				pagination={{
					enabled: true,
					manualPagination: true,
					rowCount: pagination.totalRows,
					defaultPageSize: 10,
					pageSizeOptions: [10, 20, 50],
				}}
				stickyHeader={true}
				maxHeight="calc(100vh - 320px)"
				paginationState={paginationState}
				onPaginationChange={handlePaginationChange}
			/>
			<Modal
				isOpen={showDeleteModal}
				size="s"
				closeOnOverlayClick={true}
				closeOnEscape={true}
				showCloseButton={true}
				onClose={() => setShowDeleteModal(false)}
			>
				<Modal.Header>
					<div className="flex column items-center">
						<span
							className="mb-2"
							style={{
								color: '#232B36',
								fontSize: '1.2rem',
								fontWeight: 'bold',
							}}
						>
							GIS File delete?
						</span>
					</div>
				</Modal.Header>
				<Modal.Content>
					<div className="flex column items-center">
						<span
							className="mb-3"
							style={{
								color: '#74788D',
								fontSize: '1rem',
								fontWeight: '600',
								textAlign: 'center',
							}}
						>
							Are you sure to delete {gisSelect?.label} ?
						</span>
						<span>
							<Alert
								style={{
									border: 'none',
									background: 'rgba(255, 211, 211, 0.5)',
								}}
								message={
									<div
										style={{
											color: '#F46A6A',
											fontWeight: 'bolder',
										}}
									>
										Warning
									</div>
								}
								description={
									<div style={{ color: '#F46A6A' }}>By deleting this will be wiped off the map</div>
								}
								type="error"
							/>
						</span>
					</div>
				</Modal.Content>
				<div className="separator mb-2 mt-2" />
				<Modal.Footer>
					<div className="flex">
						<div className="mr-3">
							<Button
								label="No"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#F7F9FA',
									color: '#434B56',
									border: '1px solid #C4CCD5',
								}}
								onClick={() => setShowDeleteModal(false)}
							/>
						</div>

						<Button
							label="Yes"
							showIcon={false}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
							}}
							onClick={async () => {
								console.log('gisSelect', gisSelect);
								if (gisSelect && gisSelect.id) {
									await gisService.deleteGis(gisSelect?.id?.toString());
									notification.success({
										message: 'Successful deletion',
										description: 'Gis file deleted successfully',
										placement: 'topRight',
									});
									setShowDeleteModal(false);
									handleUpdateGisFiles();
								}
							}}
						/>
					</div>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default GisTable;